import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Crosschain.css';
import { useLanguage } from '../../context/LanguageContext';
import { 
  BnbIcon, 
  EthereumIcon, 
  PolygonIcon, 
  AvalancheIcon, 
  ArbitrumIcon, 
  OptimismIcon, 
  BaseIcon, 
  OpBnbIcon, 
  HucIcon, 
  LayerZeroIcon, 
  WormholeIcon, 
  StargateIcon, 
  MultichainIcon,
  TokenLogo,
  CheckIcon,
  PendingIcon,
  ErrorIcon,
  ChevronDownIcon,
  SwitchVerticalIcon,
  CloseIcon,
  SearchIcon,
  ArrowRightIcon
} from '../common/Icons';
import Toast from '../common/Toast';

// Enhanced Bridge protocols configuration with real-world parameters and real SVG icons
const BRIDGE_PROTOCOLS = {
  layerzero: {
    name: 'LayerZero',
    icon: <LayerZeroIcon size={22} />,
    fee: '0.001',
    time: '2-5 minutes',
    description: 'Omnichain interoperability protocol',
    security: 'Ultra High',
    tvl: '$2.1B',
    successRate: '99.8%',
    gasOptimized: true,
    recommended: true
  },
  wormhole: {
    name: 'Wormhole',
    icon: <WormholeIcon size={22} />,
    fee: '0.002',
    time: '5-10 minutes',
    description: 'Cross-chain bridge network',
    security: 'High',
    tvl: '$1.8B',
    successRate: '99.5%',
    gasOptimized: false,
    recommended: false
  },
  stargate: {
    name: 'Stargate',
    icon: <StargateIcon size={22} />,
    fee: '0.0008',
    time: '1-3 minutes',
    description: 'Omnichain liquidity transport',
    security: 'Ultra High',
    tvl: '$1.2B',
    successRate: '99.9%',
    gasOptimized: true,
    recommended: true
  },
  multichain: {
    name: 'Multichain',
    icon: <MultichainIcon size={22} />,
    fee: '0.0015',
    time: '3-8 minutes',
    description: 'Cross-chain router protocol',
    security: 'Medium',
    tvl: '$800M',
    successRate: '98.2%',
    gasOptimized: false,
    recommended: false
  }
};

interface CrosschainProps {
  provider?: any;
  signer?: any;
  account?: string;
  networkConfig?: any;
  connectWallet?: () => void;
}

function Crosschain({ provider, signer, account, networkConfig, connectWallet }: CrosschainProps) {
  const { t } = useLanguage();
  const [fromChain, setFromChain] = useState('mainnet');
  const [toChain, setToChain] = useState('huc');
  const [fromToken, setFromToken] = useState<any>(null);
  const [toToken, setToToken] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [balances, setBalances] = useState<Record<string, string>>({
    mainnet_BNB: '12.45',
    mainnet_USDC: '2500.00',
    mainnet_USDT: '1850.50',
    mainnet_ETH: '3.20',
    mainnet_WBNB: '5.00',
    mainnet_CAKE: '150.00',
    huc_HUC: '1000.00',
    huc_BNB: '2.50',
    huc_USDC: '500.00',
    huc_USDT: '450.00',
    ethereum_ETH: '4.80',
    ethereum_USDC: '3200.00',
    ethereum_USDT: '2100.00',
    polygon_MATIC: '850.00',
    polygon_USDC: '600.00',
    avalanche_AVAX: '45.00',
    arbitrum_ETH: '2.10',
    optimism_ETH: '1.95',
    base_ETH: '1.40',
    opbnb_BNB: '3.10',
  });
  const [showChainSelector, setShowChainSelector] = useState<'from' | 'to' | null>(null);
  const [showTokenSelector, setShowTokenSelector] = useState<'from' | 'to' | null>(null);
  const [selectedBridge, setSelectedBridge] = useState('layerzero');
  const [bridgeFee, setBridgeFee] = useState('0.001');
  const [estimatedTime, setEstimatedTime] = useState('2-5 minutes');
  const [searchQuery, setSearchQuery] = useState('');
  const [transactions, setTransactions] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
  };
  
  const selectorRef = useRef<HTMLDivElement>(null);

  // Available networks for crosschain with real SVG icons
  const CROSSCHAIN_NETWORKS: Record<string, any> = {
    mainnet: {
      name: 'BNB Chain',
      icon: <BnbIcon size={22} />,
      chainId: 56,
      color: '#f3ba2f',
      fullName: 'BNB Smart Chain (BSC)',
      nativeCurrency: 'BNB',
      avgBlockTime: 3,
      avgFee: 0.5,
      status: 'healthy',
      volume24h: '$2.1B'
    },
    huc: {
      name: 'HuanChain L2',
      icon: <HucIcon size={22} />,
      chainId: 8888,
      color: '#00dfa2',
      fullName: 'HuanChain L2 Network',
      nativeCurrency: 'HUC',
      avgBlockTime: 1.5,
      avgFee: 0.005,
      status: 'healthy',
      volume24h: '$500M'
    },
    ethereum: {
      name: 'Ethereum',
      icon: <EthereumIcon size={22} />,
      chainId: 1,
      color: '#627eea',
      fullName: 'Ethereum Mainnet',
      nativeCurrency: 'ETH',
      avgBlockTime: 12,
      avgFee: 15.2,
      status: 'healthy',
      volume24h: '$8.4B'
    },
    polygon: {
      name: 'Polygon',
      icon: <PolygonIcon size={22} />,
      chainId: 137,
      color: '#8247e5',
      fullName: 'Polygon PoS',
      nativeCurrency: 'MATIC',
      avgBlockTime: 2,
      avgFee: 0.01,
      status: 'healthy',
      volume24h: '$1.2B'
    },
    avalanche: {
      name: 'Avalanche',
      icon: <AvalancheIcon size={22} />,
      chainId: 43114,
      color: '#e84142',
      fullName: 'Avalanche C-Chain',
      nativeCurrency: 'AVAX',
      avgBlockTime: 1,
      avgFee: 0.1,
      status: 'healthy',
      volume24h: '$800M'
    },
    arbitrum: {
      name: 'Arbitrum',
      icon: <ArbitrumIcon size={22} />,
      chainId: 42161,
      color: '#28a0f0',
      fullName: 'Arbitrum One',
      nativeCurrency: 'ETH',
      avgBlockTime: 0.3,
      avgFee: 0.5,
      status: 'healthy',
      volume24h: '$1.8B'
    },
    optimism: {
      name: 'Optimism',
      icon: <OptimismIcon size={22} />,
      chainId: 10,
      color: '#ff0420',
      fullName: 'Optimism Mainnet',
      nativeCurrency: 'ETH',
      avgBlockTime: 2,
      avgFee: 0.3,
      status: 'healthy',
      volume24h: '$900M'
    },
    opbnb: {
      name: 'opBNB',
      icon: <OpBnbIcon size={22} />,
      chainId: 204,
      color: '#f3ba2f',
      fullName: 'opBNB (BNB Chain L2)',
      nativeCurrency: 'BNB',
      avgBlockTime: 1,
      avgFee: 0.001,
      status: 'healthy',
      volume24h: '$150M'
    },
    base: {
      name: 'Base',
      icon: <BaseIcon size={22} />,
      chainId: 8453,
      color: '#0052ff',
      fullName: 'Base Mainnet',
      nativeCurrency: 'ETH',
      avgBlockTime: 2,
      avgFee: 0.2,
      status: 'healthy',
      volume24h: '$600M'
    }
  };

  // CROSSCHAIN_TOKENS with CoinMarketCap IDs and real token logos
  const CROSSCHAIN_TOKENS = {
    BNB: {
      symbol: 'BNB',
      name: 'BNB',
      coinId: 1839,
      price: 310.45,
      change24h: 2.1,
      marketCap: '47.8B',
      addresses: {
        mainnet: 'BNB',
        huc: '0xB8c77482e45F1F44dE1745F52C74426C631bDD52',
        opbnb: 'BNB',
        ethereum: '0xB8c77482e45F1F44dE1745F52C74426C631bDD52',
        polygon: '0x3BA4c387f786bFEE076A58914F5Bd38d668B42c3'
      }
    },
    HUC: {
      symbol: 'HUC',
      name: 'HuanChain Token',
      coinId: null,
      price: 2.85,
      change24h: 3.2,
      marketCap: '$285M',
      addresses: {
        huc: 'HUC',
        mainnet: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        ethereum: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        polygon: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F',
        arbitrum: '0x38B0F25A12591b65bC992b8d0b2849F30689bA4F'
      }
    },
    USDC: {
      symbol: 'USDC',
      name: 'USD Coin',
      coinId: 3408,
      price: 1.0,
      change24h: 0.02,
      marketCap: '25.1B',
      addresses: {
        mainnet: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
        huc: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
        ethereum: '0xA0b86a33E6441C41578aD6a10c9D98b2B9986A3a',
        polygon: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
        avalanche: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
        arbitrum: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
        optimism: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
        opbnb: '0x9e5AAC1Ba1a2e6aEd6b32689DFcF62A509Ca96f3',
        base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
      }
    },
    USDT: {
      symbol: 'USDT',
      name: 'Tether USD',
      coinId: 825,
      price: 1.0,
      change24h: -0.01,
      marketCap: '91.2B',
      addresses: {
        mainnet: '0x55d398326f99059fF775485246999027B3197955',
        huc: '0x55d398326f99059fF775485246999027B3197955',
        ethereum: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        polygon: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
        avalanche: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
        arbitrum: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
        optimism: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
        opbnb: '0x9e5AAC1Ba1a2e6aEd6b32689DFcF62A509Ca96f3'
      }
    },
    ETH: {
      symbol: 'ETH',
      name: 'Ethereum',
      coinId: 1027,
      price: 2340.12,
      change24h: 1.8,
      marketCap: '281.5B',
      addresses: {
        ethereum: 'ETH',
        mainnet: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
        huc: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
        polygon: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',
        arbitrum: 'ETH',
        optimism: 'ETH',
        base: 'ETH'
      }
    },
    WBNB: {
      symbol: 'WBNB',
      name: 'Wrapped BNB',
      coinId: 7192,
      price: 310.45,
      change24h: 2.1,
      marketCap: '5.2B',
      addresses: {
        mainnet: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
        huc: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
        opbnb: '0x4200000000000000000000000000000000000006'
      }
    },
    CAKE: {
      symbol: 'CAKE',
      name: 'PancakeSwap Token',
      coinId: 7186,
      price: 2.45,
      change24h: -1.2,
      marketCap: '680M',
      addresses: {
        mainnet: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82',
        huc: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82',
        ethereum: '0x152649eA73beAb28c5b49B26eb48f7EAD6d4c898'
      }
    },
    DAI: {
      symbol: 'DAI',
      name: 'Dai Stablecoin',
      coinId: 4943,
      price: 1.0,
      change24h: 0.01,
      marketCap: '4.8B',
      addresses: {
        ethereum: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
        mainnet: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3',
        huc: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3',
        polygon: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
        arbitrum: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1'
      }
    },
    MATIC: {
      symbol: 'MATIC',
      name: 'Polygon',
      coinId: 3890,
      price: 0.85,
      change24h: 2.5,
      marketCap: '8.1B',
      addresses: {
        polygon: 'MATIC',
        ethereum: '0x7D1AfA7B718fb893dB30A3aBc0Cfc608AaCfeBB0',
        mainnet: '0xCC42724C6683B7E57334c4E856f4c9965ED682bD',
        huc: '0xCC42724C6683B7E57334c4E856f4c9965ED682bD'
      }
    },
    AVAX: {
      symbol: 'AVAX',
      name: 'Avalanche',
      coinId: 5805,
      price: 28.45,
      change24h: 1.8,
      marketCap: '11.2B',
      addresses: {
        avalanche: 'AVAX',
        ethereum: '0x85f138bfEE4ef8e540890CFb48F620571d67Eda3',
        mainnet: '0x1CE0c2827e2eF14D5C4f29a091d735A204794041',
        huc: '0x1CE0c2827e2eF14D5C4f29a091d735A204794041'
      }
    }
  };

  // Initialize default tokens
  useEffect(() => {
    const usdcToken = CROSSCHAIN_TOKENS.USDC;
    setFromToken({
      ...usdcToken,
      address: usdcToken.addresses[fromChain]
    });
    setToToken({
      ...usdcToken,
      address: usdcToken.addresses[toChain]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromChain, toChain]);

  // Update bridge details when bridge is selected
  useEffect(() => {
    const bridge = BRIDGE_PROTOCOLS[selectedBridge];
    if (bridge) {
      setBridgeFee(bridge.fee);
      setEstimatedTime(bridge.time);
    }
  }, [selectedBridge]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.classList.contains('modal-overlay')) {
        setShowChainSelector(null);
        setShowTokenSelector(null);
        setSearchQuery('');
      } else if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setShowChainSelector(null);
        setShowTokenSelector(null);
        setSearchQuery('');
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowChainSelector(null);
        setShowTokenSelector(null);
        setSearchQuery('');
      }
    };

    if (showChainSelector || showTokenSelector) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
      }, 100);
      
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showChainSelector, showTokenSelector]);

  const switchChains = () => {
    const tempChain = fromChain;
    setFromChain(toChain);
    setToChain(tempChain);
    
    // Switch tokens as well
    const tempToken = fromToken;
    setFromToken({
      ...toToken,
      address: toToken?.addresses?.[toChain]
    });
    setToToken({
      ...tempToken,
      address: tempToken?.addresses?.[tempChain]
    });
  };

  const handlePercentage = (percent) => {
    const rawBalance = balances[`${fromChain}_${fromToken?.symbol}`];
    const balance = parseFloat(rawBalance) || 0;
    if (balance <= 0) return;
    let calculated = balance * (percent / 100);
    const isNative = fromToken?.isNative || fromToken?.symbol === 'BNB' || fromToken?.symbol === 'ETH';
    if (isNative && percent === 100) {
      calculated = Math.max(0, balance - 0.01);
    }
    setAmount(parseFloat(calculated.toFixed(6)).toString());
  };

  const handleMaxClick = () => handlePercentage(100);

  const handleBridge = async () => {
    if (!signer || !amount || !fromToken || !toToken) return;

    setLoading(true);
    try {
      // Professional transaction simulation with progress updates
      const steps = [
        'Preparing transaction...',
        'Validating route...',
        'Estimating gas...',
        'Submitting to bridge...',
        'Confirming transaction...'
      ];
      
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        // Could emit progress events here for UI updates
      }
      
      const newTransaction = {
        id: Date.now().toString(),
        fromChain: CROSSCHAIN_NETWORKS[fromChain].name,
        toChain: CROSSCHAIN_NETWORKS[toChain].name,
        token: fromToken.symbol,
        amount: amount,
        bridge: BRIDGE_PROTOCOLS[selectedBridge].name,
        status: 'pending',
        timestamp: new Date(),
        hash: '0x' + Math.random().toString(16).substr(2, 64),
        estimatedTime: estimatedTime,
        bridgeFee: bridgeFee,
        gasUsed: '0.002',
        priceImpact: '< 0.05%'
      };
      
      setTransactions(prev => [newTransaction, ...prev]);
      
      // Update reactive balances on source and destination chains
      const fromKey = `${fromChain}_${fromToken.symbol}`;
      const toKey = `${toChain}_${toToken.symbol}`;
      const curFrom = parseFloat(balances[fromKey] || '0');
      const curTo = parseFloat(balances[toKey] || '0');
      const transferredAmt = parseFloat(amount);
      const receivedAmt = Math.max(0, transferredAmt - parseFloat(bridgeFee));

      setBalances(prev => ({
        ...prev,
        [fromKey]: Math.max(0, curFrom - transferredAmt).toFixed(2),
        [toKey]: (curTo + receivedAmt).toFixed(2)
      }));

      // Auto update status to completed after 4s
      setTimeout(() => {
        setTransactions(prev =>
          prev.map(tx => (tx.id === newTransaction.id ? { ...tx, status: 'completed' } : tx))
        );
      }, 4000);

      // Show success notification
      showToast(t('bridge.success'), 'success');
      setAmount('');
      
    } catch (error: any) {
      console.error('Bridge failed:', error);
      showToast(t('bridge.bridgeFailed', { error: error?.message || 'Bridge transaction failed' }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const ChainSelector = ({ type, currentChain, onSelect }: { type: 'from' | 'to'; currentChain: string; onSelect: (chain: string) => void }) => {
    const modalContent = (
      <>
        <div className="modal-overlay" onClick={() => setShowChainSelector(null)} />
        <div className="chain-selector" ref={selectorRef}>
          <div className="selector-header">
            <h3 className="selector-title">
              {type === 'from' ? t('bridge.selectSourceChain') : t('bridge.selectDestChain')}
            </h3>
            <button 
              className="selector-close"
              onClick={() => setShowChainSelector(null)}
              aria-label="Close"
            >
              <CloseIcon size={16} />
            </button>
          </div>
          
          <div className="chain-list">
            {Object.entries(CROSSCHAIN_NETWORKS).map(([key, chain]) => (
              <div
                key={key}
                className={`chain-option ${currentChain === key ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(key);
                  setShowChainSelector(null);
                }}
              >
                <div className="chain-option-left">
                  <div className="chain-icon" style={{ color: chain.color }}>
                    {chain.icon}
                  </div>
                  <div className="chain-info">
                    <span className="chain-name">{chain.name}</span>
                    <span className="chain-full-name">{chain.fullName}</span>
                    <div className="chain-details">
                      <span className="chain-id">Chain ID: {chain.chainId}</span>
                      <span className="native-currency">Native: {chain.nativeCurrency}</span>
                    </div>
                  </div>
                </div>
                {currentChain === key && (
                  <div className="selected-indicator"><CheckIcon size={16} /></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    );

    return createPortal(modalContent, document.body);
  };

  const TokenSelector = ({ type, currentToken, onSelect }: { type: 'from' | 'to'; currentToken: any; onSelect: (token: any) => void }) => {
    const filteredTokens = Object.values(CROSSCHAIN_TOKENS).filter(token => {
      const targetChain = type === 'from' ? fromChain : toChain;
      return token.addresses[targetChain] && 
             (token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
              token.name.toLowerCase().includes(searchQuery.toLowerCase()));
    });

    const modalContent = (
      <>
        <div className="modal-overlay" onClick={() => { setShowTokenSelector(null); setSearchQuery(''); }} />
        <div className="token-modal">
          <div className="modal-header">
            <span>
              {t('bridge.selectToken')}
            </span>
            <button className="close-btn" onClick={() => { setShowTokenSelector(null); setSearchQuery(''); }} aria-label="Close">
              <CloseIcon size={16} />
            </button>
          </div>
          <div className="modal-divider" />
          <div className="search-box">
            <span className="search-icon-prefix"><SearchIcon size={15} color="var(--text-muted)" /></span>
            <input
              placeholder={t('swap.searchPlaceholder')}
              className="search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="token-list">
            {filteredTokens.length > 0 ? (
              filteredTokens.map(token => (
                <div
                  key={token.symbol}
                  className={`token-row${currentToken?.symbol === token.symbol ? ' selected' : ''}`}
                  onClick={() => {
                    const targetChain = type === 'from' ? fromChain : toChain;
                    onSelect({ ...token, address: token.addresses[targetChain] });
                    setShowTokenSelector(null);
                    setSearchQuery('');
                  }}
                >
                  <div className="token-icon">
                    <TokenLogo token={token} size={28} />
                  </div>
                  <div className="token-info">
                    <div className="token-symbol">{token.symbol}</div>
                    <div className="token-name">{token.name}</div>
                    <div className="token-price-info">
                      <span className="token-price">${token.price.toLocaleString()}</span>
                      <span className={`token-change ${token.change24h >= 0 ? 'positive' : 'negative'}`}>{token.change24h >= 0 ? '+' : ''}{token.change24h}%</span>
                    </div>
                  </div>
                  <div className="token-balance-section">
                    <span className="token-balance">{balances[`${type === 'from' ? fromChain : toChain}_${token.symbol}`] || '0.0000'}</span>
                    {currentToken?.symbol === token.symbol && (
                      <span className="token-selected-check">
                        <CheckIcon size={13} color="#10b981" />
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                {t('swap.noTokens')} "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      </>
    );

    return createPortal(modalContent, document.body);
  };

  return (
    <div className="crosschain-container">
      <div className="crosschain-header">
        <h2>{t('bridge.title')}</h2>
        <p>{t('bridge.subtitle')}</p>
      </div>

      <div className="bridge-main">
        <div className="bridge-form">
          {/* Visual Interactive Route Flow */}
          <div className="bridge-visual-route">
            <div className="route-chain-node">
              <span className="route-chain-icon" style={{ borderColor: CROSSCHAIN_NETWORKS[fromChain]?.color }}>
                {CROSSCHAIN_NETWORKS[fromChain]?.icon}
              </span>
              <span className="route-chain-name">{CROSSCHAIN_NETWORKS[fromChain]?.name}</span>
            </div>

            <div className="route-flow-line">
              <div className="flow-track">
                <div className="flow-pulse-particle" />
              </div>
              <div className="flow-protocol-tag">
                <span className="protocol-name">{BRIDGE_PROTOCOLS[selectedBridge]?.name}</span>
                <span className="protocol-speed">⚡ ~{estimatedTime}</span>
              </div>
            </div>

            <div className="route-chain-node">
              <span className="route-chain-icon" style={{ borderColor: CROSSCHAIN_NETWORKS[toChain]?.color }}>
                {CROSSCHAIN_NETWORKS[toChain]?.icon}
              </span>
              <span className="route-chain-name">{CROSSCHAIN_NETWORKS[toChain]?.name}</span>
            </div>
          </div>

          {/* Source Chain Section */}
          <div className="chain-section">
            <div className="section-label">{t('bridge.from')}</div>
            <div className="chain-input">
              <div 
                className="chain-select"
                onClick={() => setShowChainSelector('from')}
              >
                <div className="chain-display">
                  <span className="chain-icon" style={{ color: CROSSCHAIN_NETWORKS[fromChain].color }}>
                    {CROSSCHAIN_NETWORKS[fromChain].icon}
                  </span>
                  <span className="chain-name">{CROSSCHAIN_NETWORKS[fromChain].name}</span>
                </div>
                <span className="dropdown-arrow"><ChevronDownIcon size={14} /></span>
              </div>
              
              <div 
                className="token-select"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTokenSelector('from');
                }}
              >
                <div className="token-display">
                  {fromToken && (
                    <>
                      <span className="token-icon">
                        <TokenLogo token={fromToken} size={24} />
                      </span>
                      <span className="token-symbol">{fromToken.symbol}</span>
                    </>
                  )}
                </div>
                <span className="dropdown-arrow"><ChevronDownIcon size={14} /></span>
              </div>
            </div>
            
            <div className="amount-input">
              <input
                type="text"
                inputMode="decimal"
                pattern="^[0-9]*[.,]?[0-9]*$"
                placeholder="0.0"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^[0-9]*[.,]?[0-9]*$/.test(val)) {
                    setAmount(val.replace(',', '.'));
                  }
                }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <button className="max-button" onClick={handleMaxClick}>
                {t('common.max')}
              </button>
            </div>
            
            <div className="balance-row">
              <span 
                className="balance-text clickable"
                onClick={handleMaxClick}
                title={t('swap.balanceTooltip') || 'Click for MAX'}
              >
                {t('common.balance')}: {balances[`${fromChain}_${fromToken?.symbol}`] || '0.0000'}
              </span>
              <div className="quick-percentages">
                <button type="button" className="percent-pill" onClick={() => handlePercentage(25)}>25%</button>
                <button type="button" className="percent-pill" onClick={() => handlePercentage(50)}>50%</button>
                <button type="button" className="percent-pill" onClick={() => handlePercentage(75)}>75%</button>
                <button type="button" className="percent-pill max" onClick={handleMaxClick}>{t('common.max')}</button>
              </div>
            </div>
          </div>

          {/* Switch Button */}
          <div className="switch-section">
            <button className="switch-button" onClick={switchChains} title={t('bridge.switchChains')}>
              <SwitchVerticalIcon size={18} />
            </button>
          </div>

          {/* Destination Chain Section */}
          <div className="chain-section">
            <div className="section-label">{t('bridge.to')}</div>
            <div className="chain-input">
              <div 
                className="chain-select"
                onClick={() => setShowChainSelector('to')}
              >
                <div className="chain-display">
                  <span className="chain-icon" style={{ color: CROSSCHAIN_NETWORKS[toChain].color }}>
                    {CROSSCHAIN_NETWORKS[toChain].icon}
                  </span>
                  <span className="chain-name">{CROSSCHAIN_NETWORKS[toChain].name}</span>
                </div>
                <span className="dropdown-arrow"><ChevronDownIcon size={14} /></span>
              </div>
              
              <div 
                className="token-select"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTokenSelector('to');
                }}
              >
                <div className="token-display">
                  {toToken && (
                    <>
                      <span className="token-icon">
                        <TokenLogo token={toToken} size={24} />
                      </span>
                      <span className="token-symbol">{toToken.symbol}</span>
                    </>
                  )}
                </div>
                <span className="dropdown-arrow"><ChevronDownIcon size={14} /></span>
              </div>
            </div>
            
            <div className="estimated-amount">
              <span>{t('bridge.youWillReceive')}: </span>
              <span className="amount-value">
                {amount ? Math.max(0, parseFloat(amount) - parseFloat(bridgeFee)).toFixed(6) : '0.0'} {toToken?.symbol}
              </span>
            </div>
          </div>

          {/* Bridge Protocol Selection */}
          <div className="bridge-selection">
            <div className="section-label">{t('bridge.protocol')}</div>
            <div className="bridge-options">
              {Object.entries(BRIDGE_PROTOCOLS).map(([key, bridge]) => (
                <div
                  key={key}
                  className={`bridge-option ${selectedBridge === key ? 'selected' : ''}`}
                  onClick={() => setSelectedBridge(key)}
                >
                  <div className="bridge-header">
                    <span className="bridge-icon">{bridge.icon}</span>
                    <span className="bridge-name">{bridge.name}</span>
                    {selectedBridge === key && (
                      <span className="selected-indicator">
                        <CheckIcon size={14} color="#10b981" />
                      </span>
                    )}
                  </div>
                  <div className="bridge-details">
                    <span className="bridge-fee">{t('bridge.bridgeFee')}: {bridge.fee} {fromToken?.symbol}</span>
                    <span className="bridge-time">~{bridge.time}</span>
                  </div>
                  <div className="bridge-description">{bridge.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Bridge Info */}
          {amount && (
            <div className="bridge-info">
              <div className="info-row">
                <span>{t('bridge.transferAmount')}:</span>
                <span>{amount} {fromToken?.symbol}</span>
              </div>
              <div className="info-row">
                <span>{t('bridge.bridgeFee')}:</span>
                <span>{bridgeFee} {fromToken?.symbol}</span>
              </div>
              <div className="info-row">
                <span>{t('bridge.youllReceive')}:</span>
                <span>{(parseFloat(amount) - parseFloat(bridgeFee)).toFixed(6)} {toToken?.symbol}</span>
              </div>
              <div className="info-row">
                <span>{t('bridge.estimatedTime')}:</span>
                <span>{estimatedTime}</span>
              </div>
            </div>
          )}

          {/* Bridge Button */}
          {account ? (
            <button
              className="bridge-button"
              onClick={handleBridge}
              disabled={loading || !amount || !fromToken || !toToken}
            >
              {loading ? t('bridge.bridging') : t('bridge.bridgeAction')}
            </button>
          ) : (
            <button
              className="bridge-button connect-trigger"
              onClick={connectWallet}
            >
              {t('common.connectWallet')}
            </button>
          )}
        </div>

        {/* Transaction History */}
        {transactions.length > 0 && (
          <div className="transaction-history">
            <h3>{t('bridge.recentTransactions')}</h3>
            <div className="transaction-list">
              {transactions.slice(0, 5).map((tx) => (
                <div key={tx.id} className="transaction-item">
                  <div className="transaction-info">
                    <div className="transaction-route">
                      <span>{CROSSCHAIN_NETWORKS[tx.fromChain]?.name || tx.fromChain}</span>
                      <ArrowRightIcon size={12} color="var(--accent-color)" />
                      <span>{CROSSCHAIN_NETWORKS[tx.toChain]?.name || tx.toChain}</span>
                    </div>
                    <div className="transaction-details">
                      {tx.amount} {tx.token} via {tx.bridge}
                    </div>
                  </div>
                  <div className="transaction-status">
                    <span className={`status ${tx.status}`}>
                      {tx.status === 'pending' ? <PendingIcon size={14} /> : tx.status === 'completed' ? <CheckIcon size={14} /> : <ErrorIcon size={14} />}
                      <span>{tx.status}</span>
                    </span>
                    <div className="transaction-hash">
                      {tx.hash}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showChainSelector && (
        <ChainSelector
          type={showChainSelector}
          currentChain={showChainSelector === 'from' ? fromChain : toChain}
          onSelect={showChainSelector === 'from' ? setFromChain : setToChain}
        />
      )}

      {showTokenSelector && (
        <TokenSelector
          type={showTokenSelector}
          currentToken={showTokenSelector === 'from' ? fromToken : toToken}
          onSelect={showTokenSelector === 'from' ? setFromToken : setToToken}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Crosschain; 